// Vendor Program v2 PR 1 — per-goal detail drawer: description, AI citations
// (the human-in-the-loop provenance contract), lifecycle actions, and the
// AuditLog history (creation / edits / status changes / assignment trail).
// Deep-linkable via ?tab=goals&goal=<id>.

import { useState } from "react";
import { FileText, Sparkles } from "lucide-react";
import { Button, StatusBadge } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import type { LoanGoalDto } from "@/api/portfolioAdmin/loanGoalsApi";
import {
  useAchieveLoanGoal,
  useApproveLoanGoal,
  useDiscardLoanGoal,
  useLoanGoalActivity,
  useRetireLoanGoal,
  useUndiscardLoanGoal,
} from "@/hooks/portfolioAdmin/useLoanGoals";
import {
  GOAL_PRIORITY_BADGE,
  GOAL_STATUS_BADGE,
  vendorTypeLabel,
} from "./goalPresentation";
import { GoalFormModal } from "./GoalFormModal";

interface GoalDrawerProps {
  loanId: string;
  goal: LoanGoalDto;
  canManage: boolean;
  open: boolean;
  onClose: () => void;
}

const CITATION_SOURCE_LABEL: Record<string, string> = {
  document: "Underwriting document",
  "generated-report": "Report system",
};

export function GoalDrawer({ loanId, goal, canManage, open, onClose }: GoalDrawerProps) {
  const { tz } = useDateFormatter();
  const toast = useToast();
  const approve = useApproveLoanGoal(loanId);
  const achieve = useAchieveLoanGoal(loanId);
  const retire = useRetireLoanGoal(loanId);
  const discard = useDiscardLoanGoal(loanId);
  const undiscard = useUndiscardLoanGoal(loanId);
  const activity = useLoanGoalActivity(open ? goal.id : undefined);
  const [editOpen, setEditOpen] = useState(false);
  const pending =
    approve.isPending || achieve.isPending || retire.isPending ||
    discard.isPending || undiscard.isPending;
  const terminal =
    goal.status === "achieved" || goal.status === "retired" || goal.status === "discarded";

  const run = async (
    fn: () => Promise<unknown>,
    success: string,
  ) => {
    try {
      await fn();
      toast.success(success);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "The action failed.");
    }
  };

  const badge = GOAL_STATUS_BADGE[goal.status];

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={goal.title}
      subtitle={`Goal · ${vendorTypeLabel(goal.suggestedVendorType)}`}
      width="lg"
      testId="goal-drawer"
      footer={
        canManage && goal.status === "discarded" ? (
          <div className="flex justify-end gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void run(
                  () => undiscard.mutateAsync({ goalId: goal.id }),
                  "Goal restored to proposed.",
                )
              }
              disabled={pending}
              data-testid="goal-drawer-undiscard"
            >
              Restore to proposed
            </Button>
          </div>
        ) : canManage && !terminal ? (
          <div className="flex justify-end gap-space-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditOpen(true)}
              disabled={pending}
              data-testid="goal-drawer-edit"
            >
              Edit
            </Button>
            {goal.status === "active" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void run(() => retire.mutateAsync({ goalId: goal.id }), "Goal retired.")
                }
                disabled={pending}
                data-testid="goal-drawer-retire"
              >
                Retire
              </Button>
            )}
            {goal.status === "proposed" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void run(
                    () => discard.mutateAsync({ goalId: goal.id, reason: null }),
                    "Goal discarded.",
                  )
                }
                disabled={pending}
                data-testid="goal-drawer-discard"
              >
                Discard
              </Button>
            )}
            {goal.status === "proposed" && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  void run(
                    () => approve.mutateAsync({ goalId: goal.id }),
                    "Goal published to the borrower.",
                  )
                }
                disabled={pending}
                data-testid="goal-drawer-approve"
              >
                Approve &amp; publish
              </Button>
            )}
            {goal.status === "active" && (
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  void run(
                    () => achieve.mutateAsync({ goalId: goal.id }),
                    "Goal marked achieved.",
                  )
                }
                disabled={pending}
                data-testid="goal-drawer-achieve"
              >
                Mark achieved
              </Button>
            )}
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex items-center gap-space-2 flex-wrap">
          <StatusBadge tone={badge.tone} struck={goal.status === "retired" || goal.status === "discarded"}>
            {badge.label}
          </StatusBadge>
          <StatusBadge tone={GOAL_PRIORITY_BADGE[goal.priority].tone}>
            {GOAL_PRIORITY_BADGE[goal.priority].label} priority
          </StatusBadge>
          {goal.source === "ai" && (
            <StatusBadge tone="progress">
              <Sparkles size={12} aria-hidden /> AI-proposed
            </StatusBadge>
          )}
          <StatusBadge tone={goal.assignedVendorName ? "neutral" : "warn"}>
            {goal.assignedVendorName ?? "Unassigned"}
          </StatusBadge>
        </div>

        <section>
          <h4 className="font-display font-bold text-small m-0 mb-space-1">Description</h4>
          <p className="text-small m-0 whitespace-pre-wrap">{goal.description}</p>
        </section>

        {goal.completionCriteria && (
          <section data-testid="goal-done-when">
            <h4 className="font-display font-bold text-small m-0 mb-space-1">Done when</h4>
            <p className="text-small m-0 whitespace-pre-wrap">{goal.completionCriteria}</p>
            {goal.targetMonths && (
              <p className="text-caption text-gray-mid m-0 mt-space-1">
                Target window: months {goal.targetMonths}
              </p>
            )}
          </section>
        )}
        {goal.overlapNote && (
          <p
            className="text-small m-0 rounded-card border border-status-info bg-status-info/10 px-space-3 py-space-2"
            data-testid="goal-overlap-note"
          >
            {goal.overlapNote}
          </p>
        )}

        <section>
          <h4 className="font-display font-bold text-small m-0 mb-space-1">
            Monthly progress
          </h4>
          <p className="text-small text-gray-mid italic m-0">
            No grades yet — monthly grade history arrives with vendor reports.
          </p>
        </section>

        {goal.source === "ai" && (
          <section data-testid="goal-citations">
            <h4 className="font-display font-bold text-small m-0 mb-space-1">
              Source citations
            </h4>
            {goal.citations.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                The AI run recorded no citations for this goal.
              </p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
                {goal.citations.map((c, i) => (
                  <li
                    key={`${c.reference}-${i}`}
                    className="rounded-card border border-border-light bg-white-warm px-space-3 py-space-2"
                  >
                    <div className="flex items-center gap-space-1 text-small font-medium">
                      <FileText size={14} aria-hidden className="text-gray-mid shrink-0" />
                      <span>{c.reference}</span>
                    </div>
                    <p className="text-caption text-gray-mid m-0 mt-space-1">
                      {CITATION_SOURCE_LABEL[c.sourceType] ?? c.sourceType}
                      {c.excerpt ? <> — “{c.excerpt}”</> : null}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section data-testid="goal-history">
          <h4 className="font-display font-bold text-small m-0 mb-space-1">History</h4>
          {activity.isLoading && (
            <p className="text-small text-gray-mid italic m-0">Loading history…</p>
          )}
          {activity.isError && (
            <p className="text-small text-status-error m-0">
              Could not load the history. Refresh to retry.
            </p>
          )}
          {activity.data && activity.data.length === 0 && (
            <p className="text-small text-gray-mid italic m-0">No history yet.</p>
          )}
          {activity.data && activity.data.length > 0 && (
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
              {activity.data.map((a, i) => (
                <li key={`${a.timestamp}-${i}`} className="text-small">
                  <span className="text-gray-mid">{formatDateInTz(a.timestamp, tz)}</span>
                  {" — "}
                  {a.actionDescription}
                  {a.userDisplayName ? (
                    <span className="text-gray-mid"> · {a.userDisplayName}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {editOpen && (
        <GoalFormModal
          loanId={loanId}
          goal={goal}
          open={editOpen}
          onClose={() => setEditOpen(false)}
        />
      )}
    </ResourceDrawer>
  );
}
